<template>
  <section class="encounter-editor-section px-6 pt-4 pb-8">
    <!-- Import Recording Modal -->
    <ImportRecordingModal
      v-model="showImportRecordingModal"
      :loading="isImporting"
      :error="importError"
      :error-type="importErrorType"
      @import="handleImportRecording"
      @close="clearImportError"
    />

    <!-- Manage Prompts Modal -->
    <ManagePromptsModal
      v-model="showManagePromptsModal"
      :current-prompt-id="selectedPromptId"
      @apply="handleApplyPrompt"
    />

    <div class="editor-layout">
      <!-- Transcription Panel (left side) -->
      <TranscriptionPanel
        v-if="hasTranscription"
        :transcription="transcriptionText"
        :summary="summaryText"
        :duration="recordingDuration"
        :recording-url="tempRecordingUrl"
        :loading="isRegenerating"
        :switching="isSessionSwitching"
        :audio-loading="isAudioLoading"
        :is-draft="isDraft"
        :is-locked="isEditLocked"
        :sessions="allSessions"
        :selected-session-index="selectedSessionIndex"
        @insert="insertContent"
        @regenerate="regenerateSummary"
        @request-audio="fetchAudioOnDemand"
        @tab-change="handleTabChange"
        @manage-prompts="showManagePromptsModal = true"
        @select-session="handleSelectSession"
      />

      <!-- Rich Editor (right side) -->
      <div class="editor-container">
        <RichEditor
          ref="richEditorRef"
          :placeholder="placeholder"
          :model-value="modelValue"
          :save="save"
          :save-status="saveStatus"
          :loading="isTranscribing"
          :is-draft="isDraft && !isEditLocked"
          :has-transcription="hasTranscription"
          :patient-variables="patientVariables"
          :patient-match-data="patientMatchData"
          v-bind="$attrs"
          @record="emit('record')"
          @update:modelValue="emit('update:modelValue', $event)"
          @save="emit('save', $event)"
          @slashCommand="emit('slashCommand', $event)"
          @newRecording="handleNewRecording"
          @editTask="emit('editTask', $event)"
          @deleteTask="emit('deleteTask', $event)"
          @editAppointment="emit('editAppointment', $event)"
          @cancelAppointment="emit('cancelAppointment', $event)"
          @previewDocument="emit('previewDocument', $event)"
          @deleteDocument="emit('deleteDocument', $event)"
          @seeOrderDetails="emit('seeOrderDetails', $event)"
          @seeFormDetails="emit('seeFormDetails', $event)"
        >
          <template #actions="{ record }">

            <!-- When signed/not draft or locked by another provider, hide all action buttons -->
            <template v-if="!isDraft || isEditLocked">
              <!-- No buttons shown for signed notes or locked notes -->
            </template>

            <!-- Active recording controls (paused / in-progress) -->
            <template v-else-if="recordingState === 'paused'">
              <div
                class="flex items-center gap-3 flex-wrap bg-white border-[#81B0E1] text-[14px] font-bold py-2 px-4 border-2 border-solid rounded-[4px]"
              >
                <div
                  class="flex items-center cursor-pointer gap-0.5 border-r border-[#C4C4C4] pr-3"
                  @click="resumeRecording(record)"
                  @mouseenter="isRecordingHovered = true"
                  @mouseleave="isRecordingHovered = false"
                >
                  <RecordingIcon
                    class="w-6 h-6"
                    :stroke="isRecordingHovered ? '#5587BC' : '#81B0E1'"
                  />
                  <div class="text-[#81B0E1] hover:text-[#5587BC] underline">Continue Recording</div>
                </div>
                <div
                  class="flex items-center cursor-pointer gap-0.5"
                  @click="finishRecording"
                  @mouseenter="isFinishHovered = true"
                  @mouseleave="isFinishHovered = false"
                >
                  <FinishedIcon
                    class="w-6 h-6"
                    :stroke="isFinishHovered ? '#20A7C1' : '#1E4670'"
                  />
                  <div class="text-[#1E4670] hover:text-[#20A7C1] underline">Finish &amp; Review</div>
                </div>
              </div>
            </template>
            <template v-else-if="recordingState === 'recording'">
              <button
                type="button"
                class="in-progress-btn"
                @click="pauseRecording"
              >
                <span>
                  <img
                    class="w-8 h-8"
                    src="@/assets/encounterNotes/paused.svg"
                  />
                </span>
                <span>In Progress</span>
                <span>{{ formattedElapsed }}</span>
              </button>
            </template>

            <!-- Default state: always show Import + New Recording buttons -->
            <template v-else>
              <div class="flex items-center gap-3">
                <ResponsiveButton
                  button_type="outlined"
                  built_in_theme="light"
                  :disabled="isTranscribing || isSessionSwitching"
                  @click-button="showImportRecordingModal = true"
                >
                  <template #prefix>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="import-icon">
                      <path d="M21.9918 8.81834C21.9918 8.79509 21.9853 8.77848 21.9821 8.75523C21.995 8.62903 21.9627 8.49618 21.8626 8.39322L21.7045 8.23048C21.3817 7.89836 21.0589 7.56624 20.7329 7.23079C19.6935 6.16136 18.6541 5.09192 17.6115 4.01917L16.3138 2.68404C16.1395 2.50469 15.9749 2.29877 15.7845 2.13936C15.7038 2.05633 15.6037 2.01979 15.5036 2.01647C15.3293 1.98658 15.1356 2.00651 14.9646 2.00651H5.43898C5.11619 2.00651 4.79985 1.99986 4.47706 2.05633C3.42475 2.23567 2.54676 3.02944 2.18523 4.05238C1.927 4.78637 2.01415 5.6665 2.01415 6.4337V18.43C2.01415 18.596 2.01415 18.7621 2.01415 18.9315C2.03029 20.0341 2.59841 21.0737 3.54742 21.6117C4.33826 22.0601 5.19688 21.997 6.06197 21.997H17.9827C18.8671 21.997 19.7419 22.0567 20.5392 21.5619C21.4398 21.0039 21.9659 20.0042 21.9885 18.9315V11.9004C21.9885 10.8742 22.0143 9.84127 21.9885 8.81502L21.9918 8.81834ZM15.9168 3.55752C17.4372 5.12182 18.9575 6.68611 20.4779 8.25041L20.494 8.26701C19.3675 8.27366 18.2409 8.28362 17.1144 8.26369C16.4526 8.25373 15.9362 7.74226 15.9168 7.05477V3.55752ZM18.9801 21.0969C18.6024 21.1035 18.2215 21.0969 17.8406 21.0969H6.75597C5.52613 21.0969 3.99932 21.346 3.23108 20.0972C2.91151 19.5791 2.90183 19.0344 2.90183 18.4532V5.53365C2.90183 4.94579 2.9212 4.39447 3.2569 3.87636C3.72818 3.14569 4.48351 2.91652 5.28404 2.91652H15.0356V7.05145C15.0582 7.92161 15.5521 8.69545 16.3461 9.02093C16.7141 9.17039 17.0885 9.16707 17.4727 9.16707H21.1105V18.9348C21.0879 20.1504 20.1518 21.077 18.9801 21.0936V21.0969Z" fill="currentColor"/>
                      <path d="M16.2654 15.225V17.7491H7.74694V15.225C7.74694 14.6404 6.86572 14.6404 6.86572 15.225V18.2008C6.86572 18.4466 7.06908 18.6525 7.30471 18.6525H16.7044C16.9433 18.6525 17.1434 18.4432 17.1434 18.2008V15.225C17.1434 14.6404 16.2622 14.6404 16.2622 15.225H16.2654Z" fill="currentColor"/>
                      <path d="M7.30471 9.16707H11.6366V15.2416C11.0459 14.6404 10.4584 14.036 9.86768 13.4348C9.46419 13.023 8.84121 13.664 9.2447 14.0758C10.0678 14.9161 10.8909 15.7564 11.7108 16.5933C11.756 16.6597 11.8206 16.7096 11.8948 16.7395C12.0594 16.8192 12.2628 16.7959 12.3984 16.6597C13.2344 15.7996 14.0737 14.936 14.9097 14.0758C15.31 13.664 14.687 13.023 14.2867 13.4348L12.5178 15.2549V8.71206C12.5178 8.46629 12.3144 8.26037 12.0788 8.26037H7.30471C6.7366 8.26037 6.7366 9.16707 7.30471 9.16707Z" fill="currentColor"/>
                      <path d="M7.65978 7.66919C7.91802 7.66919 8.12461 7.45331 8.12461 7.19094C8.12461 6.92856 7.91479 6.71268 7.65978 6.71268C7.40478 6.71268 7.19497 6.92856 7.19497 7.19094C7.19497 7.45331 7.40478 7.66919 7.65978 7.66919Z" fill="currentColor"/>
                      <path d="M9.5578 7.66919C9.81604 7.66919 10.0226 7.45331 10.0226 7.19094C10.0226 6.92856 9.81281 6.71268 9.5578 6.71268C9.3028 6.71268 9.09298 6.92856 9.09298 7.19094C9.09298 7.45331 9.3028 7.66919 9.5578 7.66919Z" fill="currentColor"/>
                      <path d="M11.4558 7.66919C11.7141 7.66919 11.9206 7.45331 11.9206 7.19094C11.9206 6.92856 11.7108 6.71268 11.4558 6.71268C11.2008 6.71268 10.991 6.92856 10.991 7.19094C10.991 7.45331 11.2008 7.66919 11.4558 7.66919Z" fill="currentColor"/>
                    </svg>
                  </template>
                  Import Recording
                </ResponsiveButton>
                <ResponsiveButton
                  built_in_theme="light"
                  :disabled="isTranscribing || isSessionSwitching"
                  @click-button="startRecording(record)"
                >
                  <template #prefix>
                    <img src="@/assets/encounterNotes/icon_recording.svg">
                  </template>
                  New Recording
                </ResponsiveButton>
              </div>
            </template>
          </template>
        </RichEditor>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import RichEditor from "./RichEditor.vue";
import RecordingIcon from "./RecordingIcon.vue";
import FinishedIcon from "./FinishedIcon.vue";
import TranscriptionPanel from "./TranscriptionPanel.vue";
import ManagePromptsModal from "./ManagePromptsModal.vue";
import ImportRecordingModal from "./ImportRecordingModal.vue";

import { useRecording } from "../composables/useRecording";
import { useTranscription } from "../composables/useTranscription";
import { useImportRecording } from "../composables/useImportRecording";
import { useSessionManager } from "../composables/useSessionManager";
import {
  batchUploadApi,
  getBatchStatusApi,
} from "@/api/recordingApi";

const props = defineProps({
  placeholder: { type: String, default: "Type here to reply ..." },
  modelValue: { type: String, default: "" },
  save: { type: Function, default: null },
  saveStatus: { type: String, default: "" },
  encounterNoteId: { type: String, default: "" },
  patientId: { type: String, default: "" },
  isDraft: { type: Boolean, default: true },
  isEditLocked: { type: Boolean, default: false },
  hasRecording: { type: Boolean, default: false },
  recordings: { type: Array, default: () => [] },
  patientVariables: { type: Array, default: () => [] },
  patientMatchData: { type: Object, default: null },
});

const emit = defineEmits([
  "record", "pause", "resume", "finish",
  "update:modelValue", "save", "slashCommand",
  "uploadError", "uploadSuccess",
  "editTask", "deleteTask", "editAppointment", "cancelAppointment",
  "previewDocument", "deleteDocument", "seeOrderDetails", "seeFormDetails",
]);

const richEditorRef = ref(null);
const patientIdRef = computed(() => props.patientId);
const encounterNoteIdRef = computed(() => props.encounterNoteId);
const recordingsRef = computed(() => props.recordings);

// --- Recording composable ---
const recording = useRecording({
  patientId: patientIdRef,
  encounterNoteId: encounterNoteIdRef,
  onSessionCreated: ({ id }) => {
    sessionManager.pushSession(id);
  },
  onRecordingFinished: () => {
    transcription.getTranscription();
  },
});

// --- Transcription composable ---
const transcription = useTranscription({
  sessionId: recording.sessionId,
  recordings: recordingsRef,
  onTranscriptionReady: () => {
    emit("finish");
  },
});

// --- Session manager composable ---
const sessionManager = useSessionManager({
  sessionId: recording.sessionId,
  tempRecordingUrl: recording.tempRecordingUrl,
  clearTempRecording: recording.clearTempRecording,
  getTranscription: transcription.getTranscription,
  resetSummaryState: transcription.resetSummaryState,
});

// --- Import recording composable ---
const importRecording = useImportRecording({
  patientId: patientIdRef,
  encounterNoteId: encounterNoteIdRef,
  onImportStarted: ({ sessionId: newSessionId }) => {
    sessionManager.pushSession(newSessionId);
    // Clear any local recording blob — Zoom imports have no local audio,
    // so playback must fetch from remote via getSessionAudioApi
    recording.clearTempRecording();
    transcription.resetSummaryState();
    // Set session ID and poll via useTranscription (panel loading only, not full editor overlay)
    recording.sessionId.value = newSessionId;
    transcription.getTranscription();
  },
});

// --- Destructure for template bindings ---
const {
  recordingState, formattedElapsed, recordingDuration, tempRecordingUrl,
  isRecordingHovered, isFinishHovered,
  startRecording, pauseRecording, resumeRecording, finishRecording,
} = recording;

const {
  isTranscribing, isSessionSwitching, hasTranscription, transcriptionText,
  summaryText, isRegenerating, selectedPromptId, showManagePromptsModal,
  regenerateSummary, handleTabChange, handleApplyPrompt,
} = transcription;

const {
  allSessions, selectedSessionIndex, isAudioLoading,
  handleSelectSession, fetchAudioOnDemand,
} = sessionManager;

const {
  showImportRecordingModal, isImporting, importError, importErrorType,
  handleImportRecording, clearImportError,
} = importRecording;


// --- Local methods ---
const insertContent = (content) => {
  richEditorRef.value?.insertContent(content);
};

const handleNewRecording = () => {
  recording.resetRecordingState();
  transcription.resetTranscriptionState();
};

// --- Batch upload (kept locally as it's rarely used) ---
const isBatchUploading = ref(false);
const batchUploadProgress = ref(null);

const handleBatchUpload = async (file) => {
  if (!props.patientId) return;
  isBatchUploading.value = true;
  try {
    const formData = new FormData();
    formData.append('audio_file', file);
    formData.append('patient_id', props.patientId);
    if (props.encounterNoteId) {
      formData.append('encounter_id', props.encounterNoteId);
    }
    const response = await batchUploadApi(formData);
    recording.sessionId.value = response.session_id;
    await pollBatchStatus(response.session_id);
  } catch (error) {
    console.error("Batch upload failed:", error);
  } finally {
    isBatchUploading.value = false;
    batchUploadProgress.value = null;
  }
};

const pollBatchStatus = async (batchSessionId) => {
  const maxAttempts = 60;
  let attempts = 0;
  while (attempts < maxAttempts) {
    const status = await getBatchStatusApi(batchSessionId);
    batchUploadProgress.value = status.progress;
    if (status.status === 'completed' || status.status === 'partial_success') {
      recording.isLocalRecording.value = true;
      await transcription.getTranscription();
      return;
    }
    if (status.status === 'failed') throw new Error('Batch processing failed');
    await new Promise(resolve => setTimeout(resolve, 5000));
    attempts++;
  }
  throw new Error('Batch processing timeout');
};

// --- Watch for hasRecording/encounterNoteId changes ---
watch(
  () => [props.hasRecording, props.encounterNoteId],
  ([, newNoteId], oldVal) => {
    if (oldVal !== undefined && newNoteId !== oldVal[1]) {
      transcription.resetTranscriptionState();
      recording.recordingDuration.value = 0;
      recording.isLocalRecording.value = false;
      recording.sessionId.value = null;
      recording.sessionStatus.value = 'idle';
      recording.clearTempRecording();
      sessionManager.resetSessions();
    }
    sessionManager.initializeFromRecordings(
      props.encounterNoteId,
      recording.isLocalRecording.value
    );
  },
  { immediate: true, deep: true }
);

// --- Expose methods to parent ---
defineExpose({
  waitForPendingUploads: recording.waitForPendingUploads,
  insertContent: (content) => richEditorRef.value?.insertContent(content),
  insertCardAction: (attrs) => richEditorRef.value?.insertCardAction(attrs),
  updateCardAction: (attrs) => richEditorRef.value?.updateCardAction(attrs),
  removeCardAction: (attrs) => richEditorRef.value?.removeCardAction(attrs),
  markCardAsCancelled: (attrs) => richEditorRef.value?.markCardAsCancelled(attrs),
  focus: () => richEditorRef.value?.focus(),
});
</script>

<style scoped>
/* Editor Layout */
.editor-layout {
  display: flex;
  align-items: stretch;
  gap: 9px;
  width: 100%;
  min-height: 500px;
  margin-bottom: 2px;
}

.editor-container {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background-color: #22c55e;
}

.recording-timer {
  font-size: 13px;
  color: #0b3454;
}

.in-progress-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 42px;
  width: 350px;
  border-radius: 4px;
  border: none;
  background: #e5f9ef;
  color: #20a7c1;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.25px;
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.1s ease;
  display: flex;
  justify-content: center;
  align-items: center;
}

.in-progress-btn:hover {
  background: #d6f2e6;
}

.in-progress-btn:active {
  transform: translateY(1px);
}

/* Encounter Editor Section */
.encounter-editor-section {
  position: relative;
}

</style>
